import { supabase } from './supabase'

/**
 * Multi-factor login with an authenticator app (TOTP, design 17.1), through Supabase Auth: enrol a factor
 * (QR code), then challenge and verify a code to raise the session to aal2. The API requires aal2 when the
 * company's policy says so.
 */
export interface TotpFactor { id: string; friendly_name: string | null; status: 'verified' | 'unverified'; created_at: string }

const fail = (e: { message: string } | null) => { if (e) throw new Error(e.message) }

export async function listTotp(): Promise<TotpFactor[]> {
  const { data, error } = await supabase().auth.mfa.listFactors()
  fail(error)
  return (data?.all ?? []).filter((f) => f.factor_type === 'totp').map((f) => ({ id: f.id, friendly_name: f.friendly_name ?? null, status: f.status as TotpFactor['status'], created_at: f.created_at }))
}

/** Does this session still need a second factor (a verified factor exists but the session is aal1)? */
export async function needsChallenge(): Promise<boolean> {
  const { data, error } = await supabase().auth.mfa.getAuthenticatorAssuranceLevel()
  if (error || !data) return false
  return data.nextLevel === 'aal2' && data.currentLevel !== 'aal2'
}

/** Starts enrolment; returns the QR code (an SVG data URL), the secret for manual entry, and the factor id. */
export async function enrolTotp(name = 'Authenticator app'): Promise<{ factorId: string; qr: string; secret: string }> {
  // A half-finished enrolment from an earlier attempt would block a new one with the same name.
  for (const f of await listTotp()) if (f.status === 'unverified') await supabase().auth.mfa.unenroll({ factorId: f.id })
  const { data, error } = await supabase().auth.mfa.enroll({ factorType: 'totp', friendlyName: `${name} ${new Date().toISOString().slice(0, 10)}` })
  fail(error)
  return { factorId: data!.id, qr: data!.totp.qr_code, secret: data!.totp.secret }
}

/** Verifies a 6-digit code for a factor (finishing enrolment, or answering a sign-in challenge). */
export async function verifyCode(factorId: string, code: string) {
  const ch = await supabase().auth.mfa.challenge({ factorId })
  fail(ch.error)
  const v = await supabase().auth.mfa.verify({ factorId, challengeId: ch.data!.id, code: code.replace(/\s/g, '') })
  if (v.error) throw new Error(/invalid/i.test(v.error.message) ? 'That code did not match. Check the time on your phone and try the newest code.' : v.error.message)
}

/** Answers the sign-in challenge with the first verified factor. */
export async function answerChallenge(code: string) {
  const f = (await listTotp()).find((x) => x.status === 'verified')
  if (!f) throw new Error('No authenticator app is set up for this sign-in.')
  await verifyCode(f.id, code)
}

/**
 * The API says `mfa_required` whether or not the person has an authenticator (it cannot tell). With a
 * verified factor the session needs the challenge (next level aal2); without one, enrolment. If Supabase
 * cannot be asked, enrolment is the safe answer: it lists factors itself and finishes either way.
 */
export async function mfaRoute(): Promise<'challenge' | 'enrol'> {
  try {
    const { data } = await supabase().auth.mfa.getAuthenticatorAssuranceLevel()
    if (data?.nextLevel === 'aal2' && data.currentLevel !== 'aal2') {
      if ((await listTotp()).some((f) => f.status === 'verified')) return 'challenge'
    }
  } catch { /* fall through */ }
  return 'enrol'
}

export async function removeFactor(factorId: string) {
  const { error } = await supabase().auth.mfa.unenroll({ factorId })
  fail(error)
}
